import { formatDateOrDash } from '@/lib/format/date';
import { formatContact, formatTextOrDash } from '@/lib/format/text';
import { formValue } from '@/lib/mappers/formValues';
import {
  FIRST_SHIPPING_SITE_KEY,
  SHIPPING_SITE_FIELDS,
  SHIPPING_SITE_KEYS_FIELD,
  SUPPLIER_FIELDS,
  emptyShippingSiteValues,
  shippingSiteFieldName,
} from '../constants/suppliers.constants';

const trimmed = (value) => (value ?? '').trim();

// GET /suppliers (list) row shape.
export const processSupplierRow = (dto) => ({
  id: dto.id,
  supplierName: formatTextOrDash(dto.supplier_name),
  supplierCode: formatTextOrDash(dto.supplierId),
  shippingSitesCount: formatTextOrDash(dto.shippingSitesCount),
  primaryContact: formatContact(dto.primaryContact?.name, dto.primaryContact?.email),
  // TODO: backend never returns updated_at yet — always renders '-'.
  lastUpdated: formatDateOrDash(dto.updated_at),
});

// `email` here is the site manager's email — the backend has only one email field per shipping site.
const shippingSiteValues = (dto, siteKey) => ({
  [shippingSiteFieldName(siteKey, SHIPPING_SITE_FIELDS.shippingFrom)]: formValue(dto.shippingFrom),
  [shippingSiteFieldName(siteKey, SHIPPING_SITE_FIELDS.siteManagerName)]: formValue(
    dto.siteManagerName,
  ),
  [shippingSiteFieldName(siteKey, SHIPPING_SITE_FIELDS.siteManagerEmail)]: formValue(dto.email),
});

// dto is output.supplier: { id, supplierId, companyName, primaryContact: {id,email,name}|null, shippingDetails[] }.
export const processSupplierForm = (dto) => {
  if (!dto) return null;

  const saved = dto.shippingDetails ?? [];
  // A supplier with no sites still opens on one blank card.
  const siteKeys = saved.length
    ? saved.map((_site, index) => String(index + 1))
    : [FIRST_SHIPPING_SITE_KEY];
  const siteValues = saved.length
    ? Object.assign({}, ...saved.map((site, index) => shippingSiteValues(site, siteKeys[index])))
    : emptyShippingSiteValues(FIRST_SHIPPING_SITE_KEY);

  return {
    id: dto.id,
    values: {
      [SUPPLIER_FIELDS.organizationName]: formValue(dto.companyName),
      [SUPPLIER_FIELDS.supplierId]: formValue(dto.supplierId),
      [SUPPLIER_FIELDS.primaryEmail]: formValue(dto.primaryContact?.email),
      [SUPPLIER_FIELDS.primaryContactName]: formValue(dto.primaryContact?.name),
      [SHIPPING_SITE_KEYS_FIELD]: siteKeys,
      ...siteValues,
    },
  };
};

// POST/PUT body: { shippingFrom: required, email: optional (site manager email), siteManagerName: optional }.
const buildShippingSiteDto = (values, siteKey) => ({
  shippingFrom: trimmed(values[shippingSiteFieldName(siteKey, SHIPPING_SITE_FIELDS.shippingFrom)]),
  siteManagerName: trimmed(
    values[shippingSiteFieldName(siteKey, SHIPPING_SITE_FIELDS.siteManagerName)],
  ),
  email: trimmed(values[shippingSiteFieldName(siteKey, SHIPPING_SITE_FIELDS.siteManagerEmail)]),
});

// POST and PUT share this body; PUT fully replaces the shipping-sites collection, never a merge.
export const buildSupplierDto = (values) => {
  const shippingDetails = (values[SHIPPING_SITE_KEYS_FIELD] ?? [])
    .map((siteKey) => buildShippingSiteDto(values, siteKey))
    .filter((site) => site.shippingFrom !== '');

  return {
    name: trimmed(values[SUPPLIER_FIELDS.organizationName]),
    supplier_id: trimmed(values[SUPPLIER_FIELDS.supplierId]),
    email: trimmed(values[SUPPLIER_FIELDS.primaryEmail]),
    userName: trimmed(values[SUPPLIER_FIELDS.primaryContactName]),
    shippingDetails,
  };
};
